import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Calendar, Tag, ArrowLeft, Clock, User, BookOpen, Share2, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Helmet } from 'react-helmet-async';
import DOMPurify from 'dompurify';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

// Blog List Page
const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    fetchPosts();
  }, [selectedCategory]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const url = selectedCategory 
        ? `${API}/blog?category=${selectedCategory}`
        : `${API}/blog`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { value: '', label: 'Tous' },
    { value: 'actualites', label: 'Actualités' },
    { value: 'guides', label: 'Guides' },
    { value: 'produits', label: 'Produits' },
    { value: 'securite', label: 'Sécurité' },
  ];

  const getCategoryLabel = (value) => {
    const cat = categories.find(c => c.value === value);
    return cat?.label || value;
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      {/* SEO Meta Tags for Blog List */}
      <Helmet>
        <title>Blog Sécurité Ajax Systems | FGS Alarme Montpellier Montpellier</title>
        <meta name="description" content="Actualités, guides et conseils sur les alarmes Ajax Systems. Comparatifs produits, astuces sécurité, nouveautés. Blog FGS Alarme Montpellier Montpellier." />
        <meta name="keywords" content="blog ajax systems, actualités alarme, guide sécurité maison, comparatif alarme, conseils vidéosurveillance, fgs security blog" />
        <link rel="canonical" href="https://maprotection-ia.fr/blog" />
        <meta property="og:title" content="Blog Sécurité Ajax Systems | FGS Alarme Montpellier" />
        <meta property="og:description" content="Actualités, guides et conseils sur les alarmes Ajax Systems." />
        <meta property="og:url" content="https://maprotection-ia.fr/blog" />
        <meta property="og:image" content="https://maprotection-ia.fr/og-image-fb.png" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Blog FGS Alarme Montpellier",
            "description": "Actualités, guides et conseils sur la sécurité Ajax Systems",
            "url": "https://maprotection-ia.fr/blog",
            "publisher": {
              "@type": "Organization",
              "name": "FGS Alarme Montpellier",
              "logo": {
                "@type": "ImageObject",
                "url": "https://maprotection-ia.fr/og-image-fb.png"
              }
            }
          }
        `}</script>
      </Helmet>

      <div className="max-w-[1400px] mx-auto px-4 md:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-4 tracking-tight">
            Blog FGS
          </h1>
          <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto font-light">
            Actualités, guides et conseils sur la sécurité Ajax Systems
          </p>
        </div>

        {/* Categories Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {categories.map(cat => (
            <button
              key={cat.value}
              onClick={() => setSelectedCategory(cat.value)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === cat.value
                  ? 'bg-white text-black'
                  : 'bg-white/5 border border-white/10 text-white/60 hover:text-white hover:border-white/20'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Posts Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-white/60 text-lg">Aucun article pour le moment</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map(post => (
              <Link
                key={post.post_id}
                to={`/blog/${post.slug}`}
                className="group bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden hover:border-white/10 transition-all"
              >
                {post.image_url ? (
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={post.image_url}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ) : (
                  <div className="aspect-video bg-[#0a0a0a] flex items-center justify-center">
                    <span className="text-4xl font-heading font-semibold text-white/10">FGS</span>
                  </div>
                )}
                
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-2 py-1 bg-emerald-500/10 text-emerald-400 rounded-full text-[10px] font-medium uppercase tracking-wider">
                      {getCategoryLabel(post.category)}
                    </span>
                    <span className="text-[10px] text-white/50 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(post.created_at).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                  
                  <h2 className="text-xl font-semibold text-white mb-2 group-hover:text-emerald-400 transition-colors line-clamp-2">
                    {post.title}
                  </h2>
                  
                  <p className="text-white/50 text-sm line-clamp-3 mb-4">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-white/50 flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {post.author_name || 'FGS'}
                    </span>
                    <span className="text-emerald-400 text-sm font-medium group-hover:underline">
                      Lire la suite →
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Blog Post Detail Page
export const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/blog/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setPost(data);
        // Fetch related posts
        const allPostsRes = await fetch(`${API}/blog`);
        if (allPostsRes.ok) {
          const allPosts = await allPostsRes.json();
          const related = allPosts
            .filter(p => p.slug !== slug)
            .slice(0, 3);
          setRelatedPosts(related);
        }
      }
    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate reading time
  const readingTime = useMemo(() => {
    if (!post?.content) return 0;
    const text = post.content.replace(/<[^>]*>/g, '');
    const words = text.split(/\s+/).length;
    return Math.max(1, Math.ceil(words / 200));
  }, [post]);

  // Extract table of contents from HTML
  const tableOfContents = useMemo(() => {
    if (!post?.content) return [];
    const headingRegex = /<h2[^>]*>(.*?)<\/h2>/gi;
    const matches = [...post.content.matchAll(headingRegex)];
    return matches.map((match, idx) => ({
      id: `section-${idx}`,
      title: match[1].replace(/<[^>]*>/g, '')
    }));
  }, [post]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black/40 pt-24 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-fgs-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-black/40 pt-24 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Article non trouvé</h1>
          <Link to="/blog">
            <Button className="bg-emerald-500">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const getCategoryLabel = (cat) => {
    const labels = {
      'actualites': 'Actualités',
      'guides': 'Guides',
      'produits': 'Produits',
      'securite': 'Sécurité',
      'comparatif': 'Comparatif',
      'technologie': 'Technologie'
    };
    return labels[cat?.toLowerCase()] || cat;
  };

  return (
    <>
      {/* SEO Meta Tags for Blog Article */}
      <Helmet>
        <title>{post.title} | Blog FGS Alarme Montpellier</title>
        <meta name="description" content={post.excerpt || post.title} />
        <meta name="keywords" content={`${post.category}, ajax systems, sécurité, alarme, ${post.title.toLowerCase().split(' ').slice(0, 5).join(', ')}`} />
        <link rel="canonical" href={`https://maprotection-ia.fr/blog/${post.slug}`} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt || post.title} />
        <meta property="og:url" content={`https://maprotection-ia.fr/blog/${post.slug}`} />
        <meta property="og:image" content={post.image_url || 'https://maprotection-ia.fr/og-image-fb.png'} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={post.created_at} />
        <meta property="article:author" content={post.author_name || 'FGS Alarme Montpellier'} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": post.excerpt || post.title,
          "image": post.image_url || 'https://maprotection-ia.fr/og-image-fb.png',
          "author": {
            "@type": "Person",
            "name": post.author_name || 'FGS Alarme Montpellier'
          },
          "publisher": {
            "@type": "Organization",
            "name": "FGS Alarme Montpellier",
            "logo": {
              "@type": "ImageObject",
              "url": "https://maprotection-ia.fr/og-image-fb.png"
            }
          },
          "datePublished": post.created_at,
          "dateModified": post.updated_at || post.created_at,
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `https://maprotection-ia.fr/blog/${post.slug}`
          }
        })}</script>
      </Helmet>
      
      <div className="min-h-screen bg-black/40 pt-24 pb-16">
        {/* Breadcrumb */}
        <div className="max-w-5xl mx-auto px-6 md:px-12 mb-6">
          <nav className="flex items-center gap-2 text-sm text-white/50">
            <Link to="/" className="hover:text-emerald-400 transition-colors">Accueil</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/blog" className="hover:text-emerald-400 transition-colors">Blog</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white truncate max-w-[200px]">{post.title}</span>
          </nav>
        </div>

        <article className="max-w-5xl mx-auto px-6 md:px-12">
          {/* Header */}
          <header className="mb-10">
            <div className="flex items-center gap-3 mb-5">
              <span className="px-4 py-1.5 bg-emerald-500 text-white rounded-full text-sm font-semibold uppercase tracking-wide">
                {getCategoryLabel(post.category)}
              </span>
            </div>
            
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
              {post.title}
            </h1>

            {/* Excerpt */}
            {post.excerpt && (
              <p className="text-xl text-white/50 mb-6 leading-relaxed">
                {post.excerpt}
              </p>
            )}
            
            <div className="flex flex-wrap items-center gap-6 text-sm text-white/50 border-y border-white/5 py-4">
              <span className="flex items-center gap-2">
                <User className="w-4 h-4 text-emerald-400" />
                <span className="text-white font-medium">{post.author_name || 'FGS Alarme Montpellier'}</span>
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-emerald-400" />
                {new Date(post.created_at).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-emerald-400" />
                {readingTime} min de lecture
              </span>
            </div>
          </header>

          {/* Featured Image */}
          {post.image_url && (
            <div className="mb-10 rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={post.image_url}
                alt={post.title}
                className="w-full h-auto max-h-[500px] object-cover"
              />
            </div>
          )}

          {/* Table of Contents (if article is long) */}
          {tableOfContents.length >= 3 && (
            <div className="mb-10 p-6 bg-white/[0.02]/50 border border-white/5 rounded-xl">
              <h3 className="flex items-center gap-2 text-lg font-bold text-white mb-4">
                <BookOpen className="w-5 h-5 text-emerald-400" />
                Dans cet article
              </h3>
              <ul className="space-y-2">
                {tableOfContents.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <span className="w-6 h-6 flex items-center justify-center bg-emerald-500/10 text-emerald-400 text-xs rounded-full font-semibold">
                      {idx + 1}
                    </span>
                    <span className="text-white/50 hover:text-white transition-colors cursor-pointer">
                      {item.title}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Content - Enhanced prose styling with better spacing */}
          <div 
            className="blog-content max-w-none space-y-6"
            style={{
              lineHeight: '1.8'
            }}
          >
            <style dangerouslySetInnerHTML={{ __html: `
              .blog-content h2 {
                font-size: 1.5rem;
                font-weight: 700;
                color: white;
                margin-top: 3rem;
                margin-bottom: 1.5rem;
                padding-bottom: 0.75rem;
                border-bottom: 1px solid rgba(255,255,255,0.1);
              }
              .blog-content h3 {
                font-size: 1.25rem;
                font-weight: 600;
                color: white;
                margin-top: 2rem;
                margin-bottom: 1rem;
              }
              .blog-content p {
                color: #A3A3A3;
                margin-bottom: 1.5rem;
                line-height: 1.8;
              }
              .blog-content ul, .blog-content ol {
                margin: 1.5rem 0;
                padding-left: 1.5rem;
              }
              .blog-content li {
                color: #A3A3A3;
                margin-bottom: 0.75rem;
                line-height: 1.7;
              }
              .blog-content strong {
                color: white;
                font-weight: 600;
              }
              .blog-content a {
                color: #22C55E;
              }
              .blog-content blockquote {
                border-left: 4px solid #22C55E;
                padding: 1rem 1.5rem;
                margin: 2rem 0;
                background: rgba(255,255,255,0.03);
                border-radius: 0 0.5rem 0.5rem 0;
                font-style: italic;
                color: #A3A3A3;
              }
              .blog-content table {
                width: 100%;
                margin: 2rem 0;
                border-collapse: collapse;
              }
              .blog-content th {
                background: rgba(255,255,255,0.05);
                padding: 0.75rem;
                text-align: left;
                color: white;
                font-weight: 600;
                border-bottom: 1px solid rgba(255,255,255,0.1);
              }
              .blog-content td {
                padding: 0.75rem;
                border-bottom: 1px solid rgba(255,255,255,0.05);
                color: #A3A3A3;
              }
              .blog-content img {
                border-radius: 0.75rem;
                margin: 2rem 0;
              }
            `}} />
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }} />
          </div>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-white/5">
              <div className="flex items-center gap-3 flex-wrap">
                <Tag className="w-5 h-5 text-emerald-400" />
                <span className="text-white font-medium mr-2">Tags :</span>
                {post.tags.map(tag => (
                  <span
                    key={tag}
                    className="px-4 py-1.5 bg-white/[0.02] border border-white/5 rounded-full text-sm text-white/50 hover:border-fgs-primary hover:text-white transition-colors cursor-pointer"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Share */}
          <div className="mt-8 flex items-center gap-4">
            <span className="flex items-center gap-2 text-white font-medium">
              <Share2 className="w-5 h-5 text-emerald-400" />
              Partager :
            </span>
            <a 
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 flex items-center justify-center bg-white/[0.02] border border-white/5 rounded-full hover:border-fgs-primary transition-colors"
            >
              <svg className="w-5 h-5 text-white/50" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </a>
            <a 
              href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(post.title)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 flex items-center justify-center bg-white/[0.02] border border-white/5 rounded-full hover:border-fgs-primary transition-colors"
            >
              <svg className="w-5 h-5 text-white/50" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            </a>
            <a 
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 flex items-center justify-center bg-white/[0.02] border border-white/5 rounded-full hover:border-fgs-primary transition-colors"
            >
              <svg className="w-5 h-5 text-white/50" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </a>
          </div>

          {/* CTA */}
          <div className="mt-12 p-8 bg-gradient-to-r from-fgs-primary/20 to-fgs-surface border border-emerald-500/20 rounded-2xl">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-1">
                <h3 className="text-2xl font-semibold text-white mb-2">
                  Besoin de conseils personnalisés ?
                </h3>
                <p className="text-white/50">
                  Nos experts Ajax Systems à Montpellier sont là pour vous accompagner dans votre projet de sécurité.
                </p>
              </div>
              <div className="flex gap-3">
                <a href="tel:0745040929">
                  <Button variant="outline" className="border-fgs-primary text-emerald-400 hover:bg-emerald-500 hover:text-white">
                    07 45 04 09 29
                  </Button>
                </a>
                <Link to="/contact">
                  <Button className="bg-emerald-500 hover:bg-emerald-500-hover">
                    Contactez-nous
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <div className="mt-16">
              <h3 className="text-2xl font-semibold text-white mb-8">
                Articles similaires
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                {relatedPosts.map(relPost => (
                  <Link
                    key={relPost.post_id}
                    to={`/blog/${relPost.slug}`}
                    className="group bg-white/[0.02] border border-white/5 rounded-xl overflow-hidden hover:border-fgs-primary/50 transition-all"
                  >
                    {relPost.image_url && (
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={relPost.image_url}
                          alt={relPost.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <span className="text-xs text-emerald-400 font-medium uppercase">
                        {getCategoryLabel(relPost.category)}
                      </span>
                      <h4 className="font-heading font-bold text-white mt-2 group-hover:text-emerald-400 transition-colors line-clamp-2">
                        {relPost.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
      </div>
    </>
  );
};

export default BlogPage;
